/**
 * WebGL2 dithering wave — adapted from 21st.dev designali-in/dithering-shader (wave + 8x8 Bayer).
 * Supports optional text mask overlay with dithered dissolve transition.
 */
(function (global) {
  'use strict';

  const declarePI =
    '\n#define TWO_PI 6.28318530718\n#define PI 3.14159265358979323846\n';

  const proceduralHash11 =
    '\n float hash11(float p) {\n p = fract(p * 0.3183099) + 0.1;\n p *= p + 19.19;\n return fract(p * p);\n }\n';

  const proceduralHash21 =
    '\n float hash21(vec2 p) {\n p = fract(p * vec2(0.3183099, 0.3678794)) + 0.1;\n p += dot(p, p + 19.19);\n return fract(p.x * p.y);\n }\n';

  const simplexNoise =
    '\nvec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }\nfloat snoise(vec2 v) {\n const vec4 C = vec4(0.211324865405187, 0.366025403784439,\n -0.577350269189626, 0.024390243902439);\n vec2 i = floor(v + dot(v, C.yy));\n vec2 x0 = v - i + dot(i, C.xx);\n vec2 i1;\n i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);\n vec4 x12 = x0.xyxy + C.xxzz;\n x12.xy -= i1;\n i = mod(i, 289.0);\n vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))\n + i.x + vec3(0.0, i1.x, 1.0));\n vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),\n dot(x12.zw, x12.zw)), 0.0);\n m = m * m;\n m = m * m;\n vec3 x = 2.0 * fract(p * C.www) - 1.0;\n vec3 h = abs(x) - 0.5;\n vec3 ox = floor(x + 0.5);\n vec3 a0 = x - ox;\n m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);\n vec3 g;\n g.x = a0.x * x0.x + h.x * x0.y;\n g.yz = a0.yz * x12.xz + h.yz * x12.yw;\n return 130.0 * dot(m, g);\n}\n';

  const vertexShaderSource =
    '#version 300 es\n' +
    'precision mediump float;\n' +
    'layout(location = 0) in vec4 a_position;\n' +
    'void main() {\n gl_Position = a_position;\n}\n';

  const fragmentShaderSource =
    '#version 300 es\n' +
    'precision mediump float;\n' +
    'uniform float u_time;\n' +
    'uniform vec2 u_resolution;\n' +
    'uniform vec4 u_colorBack;\n' +
    'uniform vec4 u_colorFront;\n' +
    'uniform float u_shape;\n' +
    'uniform float u_type;\n' +
    'uniform float u_pxSize;\n' +
    'uniform float u_colorGradient;\n' +
    'uniform float u_gradientFgScale;\n' +
    'uniform sampler2D u_textMask;\n' +
    'uniform float u_textBlend;\n' +
    'out vec4 fragColor;\n' +
    simplexNoise +
    declarePI +
    proceduralHash11 +
    proceduralHash21 +
    '\nfloat getSimplexNoise(vec2 uv, float t) {\n float noise = .5 * snoise(uv - vec2(0., .3 * t));\n noise += .5 * snoise(2. * uv + vec2(0., .32 * t));\n return noise;\n}\n' +
    '\nconst int bayer2x2[4] = int[4](0, 2, 3, 1);\n' +
    'const int bayer4x4[16] = int[16](\n 0, 8, 2, 10,\n 12, 4, 14, 6,\n 3, 11, 1, 9,\n 15, 7, 13, 5\n);\n' +
    '\nconst int bayer8x8[64] = int[64](\n 0, 32, 8, 40, 2, 34, 10, 42,\n 48, 16, 56, 24, 50, 18, 58, 26,\n 12, 44, 4, 36, 14, 46, 6, 38,\n 60, 28, 52, 20, 62, 30, 54, 22,\n 3, 35, 11, 43, 1, 33, 9, 41,\n 51, 19, 59, 27, 49, 17, 57, 25,\n 15, 47, 7, 39, 13, 45, 5, 37,\n 63, 31, 55, 23, 61, 29, 53, 21\n);\n' +
    '\nfloat getBayerValue(vec2 uv, int size) {\n ivec2 pos = ivec2(mod(uv, float(size)));\n int index = pos.y * size + pos.x;\n\n if (size == 2) {\n return float(bayer2x2[index]) / 4.0;\n } else if (size == 4) {\n return float(bayer4x4[index]) / 16.0;\n } else if (size == 8) {\n return float(bayer8x8[index]) / 64.0;\n }\n return 0.0;\n}\n' +
    '\nvoid main() {\n float t = .5 * u_time;\n vec2 uv = gl_FragCoord.xy / u_resolution.xy;\n uv -= .5;\n \n float pxSize = u_pxSize;\n vec2 pxSizeUv = gl_FragCoord.xy;\n pxSizeUv -= .5 * u_resolution;\n pxSizeUv /= pxSize;\n vec2 pixelizedUv = floor(pxSizeUv) * pxSize / u_resolution.xy;\n pixelizedUv += .5;\n pixelizedUv -= .5;\n \n vec2 shape_uv = pixelizedUv;\n vec2 dithering_uv = pxSizeUv;\n vec2 ditheringNoise_uv = uv * u_resolution;\n\n float shape = 0.;\n if (u_shape < 1.5) {\n shape_uv *= .001;\n shape = 0.5 + 0.5 * getSimplexNoise(shape_uv, t);\n shape = smoothstep(0.3, 0.9, shape);\n\n } else if (u_shape < 2.5) {\n shape_uv *= .003;\n for (float i = 1.0; i < 6.0; i++) {\n shape_uv.x += 0.6 / i * cos(i * 2.5 * shape_uv.y + t);\n shape_uv.y += 0.6 / i * cos(i * 1.5 * shape_uv.x + t);\n }\n shape = .15 / abs(sin(t - shape_uv.y - shape_uv.x));\n shape = smoothstep(0.02, 1., shape);\n\n } else if (u_shape < 3.5) {\n shape_uv *= .05;\n float stripeIdx = floor(2. * shape_uv.x / TWO_PI);\n float rand = hash11(stripeIdx * 10.);\n rand = sign(rand - .5) * pow(.1 + abs(rand), .4);\n shape = sin(shape_uv.x) * cos(shape_uv.y - 5. * rand * t);\n shape = pow(abs(shape), 6.);\n\n } else if (u_shape < 4.5) {\n shape_uv *= 4.;\n float wave = cos(.5 * shape_uv.x - 2. * t) * sin(1.5 * shape_uv.x + t) * (.75 + .25 * cos(3. * t));\n shape = 1. - smoothstep(-1., 1., shape_uv.y + wave);\n\n } else if (u_shape < 5.5) {\n float dist = length(shape_uv);\n float waves = sin(pow(dist, 1.7) * 7. - 3. * t) * .5 + .5;\n shape = waves;\n\n } else if (u_shape < 6.5) {\n float l = length(shape_uv);\n float angle = 6. * atan(shape_uv.y, shape_uv.x) + 4. * t;\n float twist = 1.2;\n float offset = pow(l, -twist) + angle / TWO_PI;\n float mid = smoothstep(0., 1., pow(l, twist));\n shape = mix(0., fract(offset), mid);\n\n } else {\n shape_uv *= 2.;\n float d = 1. - pow(length(shape_uv), 2.);\n vec3 pos = vec3(shape_uv, sqrt(d));\n vec3 lightPos = normalize(vec3(cos(1.5 * t), .8, sin(1.25 * t)));\n shape = .5 + .5 * dot(lightPos, pos);\n shape *= step(0., d);\n }\n' +
    // Text mask: blend wave shape toward the text mask using Bayer dithering naturally
    '\n if (u_textBlend > 0.0) {\n  vec2 textUv = gl_FragCoord.xy / u_resolution.xy;\n  float textShape = texture(u_textMask, textUv).r;\n  shape = mix(shape, textShape, u_textBlend);\n }\n' +
    '\n int type = int(floor(u_type));\n float dithering = 0.0;\n\n switch (type) {\n case 1: {\n dithering = step(hash21(ditheringNoise_uv), shape);\n } break;\n case 2:\n dithering = getBayerValue(dithering_uv, 2);\n break;\n case 3:\n dithering = getBayerValue(dithering_uv, 4);\n break;\n default:\n dithering = getBayerValue(dithering_uv, 8);\n break;\n }\n\n dithering -= .5;\n float res = step(.5, shape + dithering);\n\n float yNorm = gl_FragCoord.y / u_resolution.y;\n vec3 fgColor = u_colorFront.rgb * u_colorFront.a;\n float fgOpacity = u_colorFront.a;\n vec3 bgColor = u_colorBack.rgb * u_colorBack.a;\n float bgOpacity = u_colorBack.a;\n\n if (u_colorGradient > 0.5) {\n  vec3 topColor = u_colorBack.rgb * u_colorBack.a;\n  vec3 bottomColor = u_colorFront.rgb * u_colorFront.a;\n  bgColor = mix(bottomColor, topColor, yNorm);\n  fgColor = mix(bottomColor * u_gradientFgScale, bottomColor, yNorm);\n }\n\n vec3 color = fgColor * res;\n float opacity = fgOpacity * res;\n\n color += bgColor * (1. - opacity);\n opacity += bgOpacity * (1. - opacity);\n\n fragColor = vec4(color, opacity);\n}\n';

  const SHAPE_WAVE = 4;
  const TYPE_BAYER_8 = 4;

  const TEXT_STATE_WAVE = 0;
  const TEXT_STATE_IN   = 1;
  const TEXT_STATE_HOLD = 2;
  const TEXT_STATE_OUT  = 3;

  function hexToRgba(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return [0, 0, 0, 1];
    return [
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255,
      1,
    ];
  }

  function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('club-cvp-teaser-wave-gl: shader compile error', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function createProgram(gl, vs, fs) {
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vs);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fs);
    if (!vertexShader || !fragmentShader) return null;
    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('club-cvp-teaser-wave-gl: program link error', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  }

  function resolveWaveColors(opts) {
    const hasExplicitGradient = opts.colorGradient === true || opts.colorGradient === false;
    const hasTopBottom = opts.colorTop != null || opts.colorBottom != null;
    const colorTop = opts.colorTop || opts.colorBack || '#1C1C1C';
    const colorBottom = opts.colorBottom || opts.colorFront || '#FF45DC';
    let colorGradient = opts.colorGradient === true;

    if (!hasExplicitGradient && hasTopBottom && opts.colorTop != null && opts.colorBottom != null) {
      colorGradient = true;
    }

    return {
      colorTop,
      colorBottom,
      colorGradient: colorGradient ? 1 : 0,
    };
  }

  /**
   * Returns a 1×1 opaque black texture used as a no-op text mask binding.
   */
  function createFallbackTexture(gl) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /**
   * Renders `text` (supports "\n" line breaks) to an offscreen canvas at device-pixel
   * dimensions, then uploads the result as a WebGL texture.  White text on a black
   * background means sampled .r == 1 → foreground colour, .r == 0 → background.
   */
  function createTextTexture(gl, text, canvasW, canvasH, opts) {
    if (!text || canvasW < 1 || canvasH < 1) return createFallbackTexture(gl);

    const offscreen = document.createElement('canvas');
    offscreen.width  = canvasW;
    offscreen.height = canvasH;
    const ctx = offscreen.getContext('2d');

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvasW, canvasH);

    const lines      = String(text).split('\n');
    const padFrac    = opts.textPaddingX != null ? opts.textPaddingX : 0.08;
    const padding    = padFrac * canvasW;
    const maxWidth   = canvasW - padding * 2;

    let fontSize;

    if (opts.textFont) {
      // User supplied a full CSS font shorthand — use as-is; try to parse px size for line layout
      ctx.font = opts.textFont;
      const m = /(\d+(?:\.\d+)?)px/.exec(opts.textFont);
      fontSize = m ? parseFloat(m[1]) : Math.max(16, Math.floor(canvasH * 0.35));
    } else {
      const fontFamily = opts.textFontFamily || 'sans-serif';
      const fontWeight = opts.textFontWeight || 'bold';
      fontSize = opts.textFontSize != null
        ? opts.textFontSize
        : Math.max(16, Math.floor(canvasH * 0.35));

      ctx.font = fontWeight + ' ' + fontSize + 'px ' + fontFamily;

      // Auto-scale down to fit the widest line within the padded area
      let maxLineW = 0;
      for (let i = 0; i < lines.length; i++) {
        const w = ctx.measureText(lines[i]).width;
        if (w > maxLineW) maxLineW = w;
      }
      if (maxLineW > maxWidth && maxLineW > 0) {
        fontSize = Math.max(8, Math.floor(fontSize * maxWidth / maxLineW));
      }
      ctx.font = fontWeight + ' ' + fontSize + 'px ' + fontFamily;
    }

    ctx.fillStyle    = '#ffffff';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    if (lines.length === 1) {
      ctx.fillText(lines[0], canvasW / 2, canvasH / 2);
    } else {
      const lineHeight  = fontSize * 1.15;
      const totalHeight = lineHeight * lines.length;
      const startY      = (canvasH - totalHeight) / 2 + fontSize / 2;
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], canvasW / 2, startY + i * lineHeight);
      }
    }

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // UNPACK_FLIP_Y_WEBGL aligns canvas Y-axis (top-down) with WebGL UV Y-axis (bottom-up)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, offscreen);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  function readCanvasWaveOpts(canvas, opts) {
    const inputOpts = opts || {};
    const dataset = canvas.dataset || {};

    return {
      colorTop: inputOpts.colorTop || dataset.waveColorTop,
      colorBottom: inputOpts.colorBottom || dataset.waveColorBottom,
      colorBack: inputOpts.colorBack || dataset.waveColorBack,
      colorFront: inputOpts.colorFront || dataset.waveColorFront,
      colorGradient:
        inputOpts.colorGradient != null
          ? inputOpts.colorGradient
          : dataset.waveGradient === 'true'
            ? true
            : dataset.waveGradient === 'false'
              ? false
              : undefined,
      shape: inputOpts.shape != null ? inputOpts.shape : dataset.waveShape != null ? Number(dataset.waveShape) : undefined,
      ditherType:
        inputOpts.ditherType != null
          ? inputOpts.ditherType
          : dataset.waveDitherType != null
            ? Number(dataset.waveDitherType)
            : undefined,
      pxSize: inputOpts.pxSize != null ? inputOpts.pxSize : dataset.wavePxSize != null ? Number(dataset.wavePxSize) : undefined,
      speed: inputOpts.speed != null ? inputOpts.speed : dataset.waveSpeed != null ? Number(dataset.waveSpeed) : undefined,
      // Text mask options
      text:                   inputOpts.text != null ? inputOpts.text : (dataset.waveText || null),
      textFont:               inputOpts.textFont               || dataset.waveTextFont               || null,
      textFontFamily:         inputOpts.textFontFamily         || dataset.waveTextFontFamily         || null,
      textFontWeight:         inputOpts.textFontWeight         || dataset.waveTextFontWeight         || null,
      textFontSize:           inputOpts.textFontSize           != null ? inputOpts.textFontSize           : dataset.waveTextFontSize           != null ? Number(dataset.waveTextFontSize)           : null,
      textPaddingX:           inputOpts.textPaddingX           != null ? inputOpts.textPaddingX           : dataset.waveTextPaddingX           != null ? Number(dataset.waveTextPaddingX)           : null,
      textHoldDuration:       inputOpts.textHoldDuration       != null ? inputOpts.textHoldDuration       : dataset.waveTextHoldDuration       != null ? Number(dataset.waveTextHoldDuration)       : null,
      textTransitionDuration: inputOpts.textTransitionDuration != null ? inputOpts.textTransitionDuration : dataset.waveTextTransitionDuration != null ? Number(dataset.waveTextTransitionDuration) : null,
      textWaveDuration:       inputOpts.textWaveDuration       != null ? inputOpts.textWaveDuration       : dataset.waveTextWaveDuration       != null ? Number(dataset.waveTextWaveDuration)       : null,
      gradientFgScale:
        inputOpts.gradientFgScale != null
          ? inputOpts.gradientFgScale
          : dataset.waveGradientFgScale != null
            ? Number(dataset.waveGradientFgScale)
            : undefined,
    };
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {object} [opts]
   * @param {string}  [opts.colorTop]               Top wave colour (gradient) or back dither colour (flat).
   * @param {string}  [opts.colorBottom]            Bottom wave colour (gradient) or front dither colour (flat).
   * @param {string}  [opts.colorBack]              Legacy alias for colorTop.
   * @param {string}  [opts.colorFront]             Legacy alias for colorBottom.
   * @param {boolean} [opts.colorGradient]          Enable vertical gradient between top and bottom colours.
   * @param {number}  [opts.shape]                  Shape index (default: 4 = wave).
   * @param {number}  [opts.ditherType]             Dither type (default: 4 = Bayer 8×8).
   * @param {number}  [opts.pxSize]                 Pixel block size (default: 5).
   * @param {number}  [opts.speed]                  Animation speed multiplier (default: 1).
   * @param {string}  [opts.text]                   Text to dissolve in as a mask, e.g. "UP TO 50% OFF". Supports "\n" line breaks.
   * @param {string}  [opts.textFont]               Full CSS font shorthand, e.g. "bold 80px Arial". Overrides family/weight/size.
   * @param {string}  [opts.textFontFamily]         Font family (default: "sans-serif").
   * @param {string}  [opts.textFontWeight]         Font weight (default: "bold").
   * @param {number}  [opts.textFontSize]           Font size in device pixels. Auto-calculated from canvas height when omitted.
   * @param {number}  [opts.textPaddingX]           Horizontal padding as a fraction of canvas width (default: 0.08).
   * @param {number}  [opts.textHoldDuration]       Seconds to hold the text at full opacity (default: 3).
   * @param {number}  [opts.textTransitionDuration] Seconds for the dissolve in/out transition (default: 1.2).
   * @param {number}  [opts.gradientFgScale]        Gradient dither foreground scale at bottom (default: 0.7). Use 1 for full-vibrance orange/pink at bottom.
   * @returns {{ destroy: function, setColors: function } | null}
   */
  function mountClubCvpTeaserWave(canvas, opts) {
    const waveOpts = readCanvasWaveOpts(canvas, opts || {});
    const colors = resolveWaveColors(waveOpts);
    let colorTop      = colors.colorTop;
    let colorBottom   = colors.colorBottom;
    let colorGradient = colors.colorGradient;

    const pxSize    = waveOpts.pxSize    != null ? waveOpts.pxSize    : 5;
    const speed     = waveOpts.speed     != null ? waveOpts.speed     : 1;
    const shape     = waveOpts.shape     != null ? waveOpts.shape     : SHAPE_WAVE;
    const ditherType = waveOpts.ditherType != null ? waveOpts.ditherType : TYPE_BAYER_8;
    const gradientFgScale = waveOpts.gradientFgScale != null ? waveOpts.gradientFgScale : 0.7;

    // Text config
    const hasText              = !!waveOpts.text;
    const textHoldDuration     = waveOpts.textHoldDuration       != null ? waveOpts.textHoldDuration       : 3;
    const textTransitionDur    = waveOpts.textTransitionDuration != null ? waveOpts.textTransitionDuration : 1.2;
    const textWaveDuration     = waveOpts.textWaveDuration       != null ? waveOpts.textWaveDuration       : 5;

    // Text state machine
    let textState      = TEXT_STATE_WAVE;
    let textStateStart = performance.now() / 1000;
    let textBlend      = 0;

    const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false });
    if (!gl) {
      console.warn('club-cvp-teaser-wave-gl: WebGL2 not available');
      return null;
    }

    const program = createProgram(gl, vertexShaderSource, fragmentShaderSource);
    if (!program) return null;

    const positionLoc    = gl.getAttribLocation(program, 'a_position');
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

    const locs = {
      u_time:         gl.getUniformLocation(program, 'u_time'),
      u_resolution:   gl.getUniformLocation(program, 'u_resolution'),
      u_colorBack:    gl.getUniformLocation(program, 'u_colorBack'),
      u_colorFront:   gl.getUniformLocation(program, 'u_colorFront'),
      u_shape:        gl.getUniformLocation(program, 'u_shape'),
      u_type:         gl.getUniformLocation(program, 'u_type'),
      u_pxSize:       gl.getUniformLocation(program, 'u_pxSize'),
      u_colorGradient:gl.getUniformLocation(program, 'u_colorGradient'),
      u_gradientFgScale: gl.getUniformLocation(program, 'u_gradientFgScale'),
      u_textMask:     gl.getUniformLocation(program, 'u_textMask'),
      u_textBlend:    gl.getUniformLocation(program, 'u_textBlend'),
    };

    // Always keep a valid texture bound to u_textMask (1×1 black when no text)
    let textTexture = hasText
      ? createTextTexture(gl, waveOpts.text, canvas.width, canvas.height, waveOpts)
      : createFallbackTexture(gl);

    // Refresh texture once web fonts have fully loaded so custom typefaces render correctly
    if (hasText && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (textTexture) gl.deleteTexture(textTexture);
        textTexture = createTextTexture(gl, waveOpts.text, canvas.width, canvas.height, waveOpts);
      });
    }

    const start = performance.now();
    let raf = 0;
    let ro  = null;

    function resize() {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const dpr  = Math.min(window.devicePixelRatio || 1, 2);
      const w    = Math.max(1, Math.floor(rect.width  * dpr));
      const h    = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width  = w;
        canvas.height = h;
        // Rebuild text texture to match new canvas dimensions
        if (hasText) {
          if (textTexture) gl.deleteTexture(textTexture);
          textTexture = createTextTexture(gl, waveOpts.text, canvas.width, canvas.height, waveOpts);
        }
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    }

    function render() {
      raf = requestAnimationFrame(render);
      const t   = ((performance.now() - start) / 1000) * speed;
      const now = performance.now() / 1000;

      // Advance text state machine
      if (hasText) {
        const elapsed = now - textStateStart;

        if (textState === TEXT_STATE_WAVE) {
          textBlend = 0;
          if (elapsed >= textWaveDuration) {
            textState      = TEXT_STATE_IN;
            textStateStart = now;
          }
        } else if (textState === TEXT_STATE_IN) {
          textBlend = Math.min(1, elapsed / textTransitionDur);
          if (elapsed >= textTransitionDur) {
            textBlend      = 1;
            textState      = TEXT_STATE_HOLD;
            textStateStart = now;
          }
        } else if (textState === TEXT_STATE_HOLD) {
          textBlend = 1;
          if (elapsed >= textHoldDuration) {
            textState      = TEXT_STATE_OUT;
            textStateStart = now;
          }
        } else if (textState === TEXT_STATE_OUT) {
          textBlend = Math.max(0, 1 - elapsed / textTransitionDur);
          if (elapsed >= textTransitionDur) {
            textBlend      = 0;
            textState      = TEXT_STATE_WAVE;
            textStateStart = now;
          }
        }
      }

      gl.useProgram(program);
      if (locs.u_time)          gl.uniform1f(locs.u_time, t);
      if (locs.u_resolution)    gl.uniform2f(locs.u_resolution, canvas.width, canvas.height);
      if (locs.u_colorBack)     gl.uniform4fv(locs.u_colorBack, hexToRgba(colorTop));
      if (locs.u_colorFront)    gl.uniform4fv(locs.u_colorFront, hexToRgba(colorBottom));
      if (locs.u_shape)         gl.uniform1f(locs.u_shape, shape);
      if (locs.u_type)          gl.uniform1f(locs.u_type, ditherType);
      if (locs.u_pxSize)        gl.uniform1f(locs.u_pxSize, pxSize);
      if (locs.u_colorGradient) gl.uniform1f(locs.u_colorGradient, colorGradient);
      if (locs.u_gradientFgScale) gl.uniform1f(locs.u_gradientFgScale, gradientFgScale);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textTexture);
      if (locs.u_textMask)  gl.uniform1i(locs.u_textMask, 0);
      if (locs.u_textBlend) gl.uniform1f(locs.u_textBlend, textBlend);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    resize();
    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else {
      window.addEventListener('resize', resize);
    }

    raf = requestAnimationFrame(render);

    return {
      destroy: function () {
        cancelAnimationFrame(raf);
        if (ro) {
          ro.disconnect();
        } else {
          window.removeEventListener('resize', resize);
        }
        if (textTexture) gl.deleteTexture(textTexture);
        gl.deleteProgram(program);
      },
      setColors: function (nextColors) {
        const resolved = resolveWaveColors(Object.assign({}, waveOpts, nextColors || {}));
        colorTop      = resolved.colorTop;
        colorBottom   = resolved.colorBottom;
        colorGradient = resolved.colorGradient;
        Object.assign(waveOpts, nextColors || {});
      },
    };
  }

  global.mountClubCvpTeaserWave = mountClubCvpTeaserWave;
})(typeof window !== 'undefined' ? window : this);
